/**
 * AccountRecoveryPage — Lost-login recovery flow.
 * Route: /recover
 *
 * Steps:
 *   1. Email → OTP requested + emailed via Brevo
 *   2. Enter OTP → recovery_token issued
 *   3. Success — prompt to re-connect wallet via Reown (guest logs in with new
 *      email/social; we'll bind the new wallet to their recovered email)
 *
 * Fallback: "Can't access email?" → upload ID → if unique match, proceed.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, Mail, ShieldCheck, ArrowRight, ArrowLeft, KeyRound, IdCard, Check, Upload, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

const STEP_EMAIL = 'email';
const STEP_OTP = 'otp';
const STEP_ID = 'id';
const STEP_DONE = 'done';

export default function AccountRecoveryPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEP_EMAIL);
  const [email, setEmail] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [recoveryToken, setRecoveryToken] = useState(null);
  const [idImage, setIdImage] = useState(null);
  const [idPreview, setIdPreview] = useState(null);

  const call = async (path, body) => {
    const r = await fetch(`${BACKEND_URL}/api/recovery/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.detail || 'Request failed');
    return d;
  };

  const requestOtp = async () => {
    if (!email) { toast.error('Enter your email'); return; }
    setLoading(true);
    try {
      const d = await call('request-otp', { email });
      setMaskedEmail(d.masked_email);
      setStep(STEP_OTP);
      toast.success(`Code sent to ${d.masked_email}`);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const d = await call('verify-otp', { email, otp });
      setRecoveryToken(d.recovery_token);
      setStep(STEP_DONE);
      toast.success('Recovery verified');
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const onIdFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error('Image too large (max 8 MB)'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setIdPreview(dataUrl);
      setIdImage(String(dataUrl).split(',')[1] || '');
    };
    reader.readAsDataURL(f);
  };

  const recoverByIdSubmit = async () => {
    if (!idImage) { toast.error('Choose an ID photo'); return; }
    setLoading(true);
    try {
      const d = await call('by-id', { image_base64: idImage });
      setMaskedEmail(d.masked_email);
      setRecoveryToken(d.recovery_token);
      setIdImage(null); setIdPreview(null);
      setStep(STEP_DONE);
      toast.success(`Account matched · ${d.masked_email}`);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white flex items-center justify-center p-4" data-testid="account-recovery-page">
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate('/')}
          className="text-xs text-[#94a3b8] hover:text-white flex items-center gap-1 mb-4"
        >
          <ArrowLeft className="w-3 h-3" /> Back to getbitsy
        </button>

        <div className="bg-[#0d1220] border border-[#1a2235] rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#F7931A]/10 border border-[#F7931A]/30 flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-[#F7931A]" />
            </div>
            <div>
              <h1 className="text-lg font-semibold">Recover your account</h1>
              <p className="text-[11px] text-[#94a3b8]">Re-bind your wallet · keep your SBT reputation</p>
            </div>
          </div>

          {/* Step 1: Email */}
          {step === STEP_EMAIL && (
            <div className="space-y-4" data-testid="step-email">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#64748b]">Email address</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && requestOtp()}
                  placeholder="you@example.com"
                  className="mt-1 bg-[#080c16] border-[#1a2235]"
                  data-testid="recover-email-input"
                />
              </div>
              <Button
                onClick={requestOtp}
                disabled={loading}
                className="w-full bg-[#F7931A] hover:bg-[#F7931A]/90 text-black"
                data-testid="recover-request-otp-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <><Mail className="w-4 h-4 mr-2" /> Send recovery code</>
                )}
              </Button>

              <div className="relative py-3">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#1a2235]" /></div>
                <div className="relative flex justify-center"><span className="bg-[#0d1220] px-2 text-[10px] text-[#64748b]">OR</span></div>
              </div>

              <button
                onClick={() => setStep(STEP_ID)}
                className="w-full text-xs text-[#94a3b8] hover:text-white flex items-center justify-center gap-2 py-2"
                data-testid="recover-by-id-link"
              >
                <IdCard className="w-4 h-4" /> Can't access email? Recover with ID
              </button>
            </div>
          )}

          {/* Step 2: OTP */}
          {step === STEP_OTP && (
            <div className="space-y-4" data-testid="step-otp">
              <p className="text-[11px] text-[#94a3b8]">
                We sent a 6-digit code to <span className="font-mono text-white">{maskedEmail}</span>.
                Expires in 10 minutes.
              </p>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && verifyOtp()}
                placeholder="• • • • • •"
                className="bg-[#080c16] border-[#1a2235] text-center text-xl tracking-[0.5em] font-mono"
                data-testid="recover-otp-input"
              />
              <Button
                onClick={verifyOtp}
                disabled={loading || otp.length !== 6}
                className="w-full bg-[#F7931A] hover:bg-[#F7931A]/90 text-black"
                data-testid="recover-verify-otp-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>Verify code <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
              <button
                onClick={() => { setStep(STEP_EMAIL); setOtp(''); }}
                className="w-full text-[11px] text-[#64748b] hover:text-white py-1"
                data-testid="recover-otp-back"
              >
                Use a different email
              </button>
            </div>
          )}

          {/* Fallback: ID re-verify */}
          {step === STEP_ID && (
            <div className="space-y-4" data-testid="step-id">
              <p className="text-[11px] text-[#94a3b8] leading-relaxed">
                Upload the same ID you used originally. If we find a unique match, we'll surface the masked email so you can recover.
                We <strong className="text-white">never store your DOB or photo</strong>.
              </p>
              {!idPreview ? (
                <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#1a2235] hover:border-[#F7931A]/50 rounded-lg p-6 cursor-pointer transition" data-testid="recover-id-label">
                  <Upload className="w-5 h-5 text-[#94a3b8]" />
                  <p className="text-sm">Upload ID photo</p>
                  <p className="text-[10px] text-[#64748b]">Passport / ID / driver's license</p>
                  <input type="file" accept="image/*" onChange={onIdFile} className="hidden" data-testid="recover-id-file" />
                </label>
              ) : (
                <div className="relative">
                  <img src={idPreview} alt="ID" className="w-full max-h-48 object-contain rounded-lg border border-[#1a2235]" />
                  <button onClick={() => { setIdPreview(null); setIdImage(null); }} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <Button
                onClick={recoverByIdSubmit}
                disabled={loading || !idImage}
                className="w-full bg-[#F7931A] hover:bg-[#F7931A]/90 text-black"
                data-testid="recover-by-id-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <><ShieldCheck className="w-4 h-4 mr-2" /> Find my account</>
                )}
              </Button>
              <button
                onClick={() => setStep(STEP_EMAIL)}
                className="w-full text-[11px] text-[#64748b] hover:text-white py-1"
                data-testid="recover-id-back"
              >
                Back to email recovery
              </button>
            </div>
          )}

          {/* Success */}
          {step === STEP_DONE && (
            <div className="space-y-4 text-center" data-testid="step-done">
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto">
                <Check className="w-7 h-7 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-base font-semibold">Account recovered</h2>
                <p className="text-[11px] text-[#94a3b8] mt-1">
                  Recovery token active · 15 min. Sign in with your preferred wallet/social on the login page and we'll re-bind it to {maskedEmail || 'your account'}.
                </p>
              </div>
              <div className="bg-[#080c16] rounded-lg p-3 text-left">
                <p className="text-[10px] uppercase text-[#64748b] mb-1">Recovery token (keep safe)</p>
                <p className="text-[10px] font-mono break-all text-[#F7931A]" data-testid="recover-token">{recoveryToken}</p>
              </div>
              <Button
                onClick={() => navigate('/guest-login')}
                className="w-full bg-[#F7931A] hover:bg-[#F7931A]/90 text-black"
                data-testid="recover-go-login"
              >
                Go to login <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>

        {step !== STEP_DONE && (
          <p className="text-[10px] text-[#64748b] text-center mt-4">
            Your SBT reputation is preserved across recovery. We never store your ID photo or DOB.
          </p>
        )}
      </div>
    </div>
  );
}
